/** @jsxImportSource @emotion/react */

import React from "react";
import Immutable from "immutable";
import {css} from "@emotion/react";

import * as DashboardUtils from "js/dashboards/utils";
import {
  dashboardColumns,
  GAMIFIED_ASPECT_RATIOS,
  STAGE_H_PADDING,
  getGamifiedMaxRows,
  getGamifiedStageGeometry
} from "js/dashboards/utils";
import {resolveBackgroundStyle, resolvePanelSurfaceColor, resolveZoom} from "js/dashboards/settings/appearance-utils";
import * as Rata from "js/common/utils/remote-data";
import useErrorBoundary from "use-error-boundary";
import useDimensions from "js/common/utils/use-dimensions";
import {indexByJs} from "js/common/utils/collections";

import GridLayout, {WidthProvider} from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import {BasicComponentTitleBar} from "js/dashboards/components/title-bar";
import {ComponentTile} from "js/dashboards/components/grids/utils";
import * as Overlays from "js/dashboards/components/overlays";
import {TextButton} from "js/common/views/inputs/buttons";
import {CustomThemeContext} from "js/common/themes/CustomThemeProvider";

const SizedGridLayout = WidthProvider(GridLayout);
const dragHandles = ["se"];
const noDragHandles = [];
const noop = () => {};

// This style is require to allow tooltip to always be on top of other components
const componentWrapperStyle = css`
  &:hover {
    z-index: 100;
  }
`;

// NOTE: This style is required so that tooltips do no get hidden by other components.
// All alternatives surrounding Tooltip z-index have been explored, this is the nicest solution.
const componentStyle = css`
  height: 100%;
  width: 100%;

  &:hover {
    z-index: 1;
  }
;
`;

const fullScreenStyle = (theme, sidebarWidth = 0, toolbarBottomY = 112) => {
  const yOffset = window.pageYOffset;
  const top = Math.max(2, toolbarBottomY - yOffset);

  return css`
    background: ${theme.palette.background.paper};
    position: fixed !important;
    top: ${top}px;
    left: ${sidebarWidth}px;
    padding: 10px 21px;
    height: calc(100vh - ${top}px) !important;
    width: calc(100vw - ${sidebarWidth}px) !important;
    transform: none !important;
    z-index: 100;
  `;
};

const DashboardDisplay = React.memo(({
  componentIdToLoadId,
  dashboard,
  appearanceOverride = null,
  onDashboardChange = () => {},
  isEditing,
  activeTabId,
  cachedTabIds,
  idToDataByTab,
  uiStateByTab,
  componentTypeToTemplate,
  onRequestEdit,
  onRequestComponentReload,
  onDuplicateClick,
  queueDataLoad,
  clearDataLoad,
  onRequestDialog,
  onCaptureUiStateRef,
  rowHeight,
  width = 800,
  sidebarWidth,
  toolbarBottomY,
  scrollToComponentRef,
  onComponentLoadStart,
  onComponentLoadComplete,
  onOutOfBoundsChange = () => {},
  hasOutOfBoundsPanels = false,
  isPresentationMode = false,
  isScheduledReportMode = false,
}) => {
  const {theme} = React.useContext(CustomThemeContext);
  // `appearanceOverride` is the live (unsaved) preview pushed from the settings drawer;
  // when present it takes precedence over the persisted appearance.
  const savedAppearance = dashboard.getIn(["json", "appearance"])
    ? dashboard.getIn(["json", "appearance"]).toJS()
    : null;
  const appearance = appearanceOverride != null ? appearanceOverride : savedAppearance;
  // Scheduled reports must not pull in the uploaded background image asset.
  const backgroundStyle = resolveBackgroundStyle(appearance, {suppressImage: isScheduledReportMode});
  // Panel "match": a shade of the selected background colour used as each panel's
  // surface (see ComponentTile's `panelColor` prop). null when panel-match is off.
  const panelColor = resolvePanelSurfaceColor(appearance);
  const allComponents = dashboard.getIn(["json", "components"]);
  const componentsByTab = React.useMemo(
      () => (cachedTabIds || Immutable.List()).reduce(
          (m, tabId) => m.set(tabId, DashboardUtils.getComponentsForTab(allComponents, tabId)),
          Immutable.Map()),
      [allComponents, cachedTabIds]
  );
  const components = React.useMemo(
      () => DashboardUtils.getComponentsForTab(allComponents, activeTabId),
      [allComponents, activeTabId]
  );
  const componentRefs = React.useRef(Immutable.Map());

  const scrollToComponent = React.useCallback((componentId) => {
    setTimeout(() => {
      const componentRef = componentRefs.current.get(componentId);
      if (componentRef) {
        componentRef.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "nearest"
        });
      }
    }, 200); // Small delay to ensure panel is in the DOM.
  }, []);

  React.useEffect(() => {
    if (scrollToComponentRef) {
      scrollToComponentRef.current = scrollToComponent;
    }
  }, [scrollToComponent, scrollToComponentRef]);

  // A tab can be mounted while hidden (display:none → zero width) when its data
  // was background-prefetched. On reveal, nudge WidthProvider / ag-grid /
  // Highcharts to re-measure. Mirrors the resize dispatch used for the sidebar
  // and print mode in app.js.
  React.useEffect(() => {
    const id = requestAnimationFrame(() => window.dispatchEvent(new Event("resize")));
    return () => cancelAnimationFrame(id);
  }, [activeTabId]);

  // Clean up stale refs when components change (needed in-case user deletes a panel)
  React.useEffect(() => {
    const currentComponentIds = Immutable.Set(components.map(c => c.get("id")));
    componentRefs.current = componentRefs.current.filter((ref, componentId) =>
        currentComponentIds.has(componentId)
    );
  }, [components]);

  const handleComponentChange = React.useCallback((componentId, newComponentFn, reload = false) => {
    onDashboardChange(dashboard => {
      const components = dashboard.getIn(["json", "components"], Immutable.List());
      const index = components.findIndex(c => c.get("id") === componentId);
      const oldComponent = components.get(index);
      const newComponent = newComponentFn(oldComponent);
      const newComponents = components.set(index, newComponent);
      return dashboard.setIn(["json", "components"], newComponents);
    }, {reloadDashboard: reload});
  }, [onDashboardChange]);

  const handleParentConfigChange = React.useCallback((newParentConfigOrFn, reload = false) => {
    onDashboardChange(dashboard => {
      if (typeof newParentConfigOrFn === "function") {
        return dashboard.updateIn(["json", "dataConfig"], Immutable.Map(), newParentConfigOrFn);
      } else {
        return dashboard.setIn(["json", "dataConfig"], newParentConfigOrFn);
      }
    }, {reloadDashboard: reload});
  }, [onDashboardChange]);

  const handleLayoutChange = React.useCallback(dashboardLayout => {
    const componentIdToLayout = indexByJs(componentLayout => {
      const strId = componentLayout.i;
      return parseInt(strId.replace("component-", ""));
    }, dashboardLayout);

    onDashboardChange(dashboard => dashboard
        .updateIn(
            ["json", "components"],
            Immutable.List(),
            components => components
                .map(component => {
                  if (component.get("tabId") !== activeTabId) {
                    return component;
                  }
                  const newLayoutForComponent = componentIdToLayout[component.get("id")];
                  return component.update(
                      "layout",
                      Immutable.Map(),
                      layout => layout
                          .set("x", newLayoutForComponent.x)
                          .set("y", newLayoutForComponent.y)
                          .set("width", newLayoutForComponent.w)
                          .set("height", newLayoutForComponent.h));
                })));
  }, [onDashboardChange, activeTabId]);

  const handleDeleteComponent = React.useCallback((componentId, onCloseDialog) => {
    onDashboardChange(dashboard => {
      const components = dashboard.getIn(["json", "components"], Immutable.List());
      const index = components.findIndex(c => c.get("id") === componentId);
      return dashboard.deleteIn(["json", "components", index]);
    });
    onCloseDialog();
  }, [onDashboardChange]);

  const handleDeleteComponentRequest = React.useCallback(componentId => {
    const onCloseDialog = onRequestDialog(
        <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              height: "80%",
              margin: "0 10px"
            }}>
          <p style={{margin: 0}}>
            Are you sure you want to delete this panel?
          </p>
          <div style={{display: "flex", justifyContent: "flex-end"}}>
            <TextButton icon="history" label="Cancel" onClick={() => onCloseDialog()} style={{margin: "0 0.5rem"}} />
            <TextButton
                icon="trash" type="primary" label="Delete"
                testId={`${componentId}-delete-confirm`}
                onClick={() => handleDeleteComponent(componentId, onCloseDialog)} style={{margin: "0 0.5rem"}} />
          </div>
        </div>,
        {
          id: "server-grid-cell-dialog-" + Math.random(),
          title: "Delete panel?",
          width: 700,
          height: 180,
          titleStyle: {color: theme.palette.primary.main, textTransform: "uppercase", margin: 10}
        });
  }, [onRequestDialog, handleDeleteComponent, theme]);

  // Gamified layout pre-computations (always run so hook rules are satisfied)
  const isGamified = dashboard.getIn(["json", "layoutType"]) === "gamified";
  const gamifiedAspectRatio = dashboard.getIn(["json", "aspectRatio"], "16:9");
  const gamifiedRatio = GAMIFIED_ASPECT_RATIOS[gamifiedAspectRatio] || GAMIFIED_ASPECT_RATIOS["16:9"];
  const gamifiedMaxRows = getGamifiedMaxRows(gamifiedAspectRatio);
  // Scheduled reports zoom too: the layout keeps its physical size at every zoom, so the
  // page the backend sizes is unchanged and only the content it holds is scaled.
  const zoom = resolveZoom(appearance);
  const {
    stageWidth: gamifiedStageWidth,
    rowHeight: gamifiedRowHeight,
    stageHeight: gamifiedStageHeight
  } = getGamifiedStageGeometry({width, zoom, maxRows: gamifiedMaxRows});

  const handleGamifiedLayoutChange = React.useCallback(dashboardLayout => {
    handleLayoutChange(dashboardLayout);
    const outOfBounds = dashboardLayout.some(item => item.y + item.h > gamifiedMaxRows);
    onOutOfBoundsChange(outOfBounds);
  }, [handleLayoutChange, gamifiedMaxRows, onOutOfBoundsChange]);

  // Re-evaluate out-of-bounds panels whenever the row budget changes (e.g. the aspect
  // ratio is switched) so the stage border turns red immediately, rather than waiting
  // for the next layout change to fire.
  React.useEffect(() => {
    if (!isGamified) {
      return;
    }
    const outOfBounds = components.some(c => {
      const {y, h} = getLayoutForComponent(c, componentTypeToTemplate);
      return y + h > gamifiedMaxRows;
    });
    onOutOfBoundsChange(outOfBounds);
  }, [isGamified, components, componentTypeToTemplate, gamifiedMaxRows, onOutOfBoundsChange]);

  // Changing `zoom` relays out every panel but fires no resize event, and the charts and
  // grids inside panels only re-measure on one. Same kick used when a tab is revealed
  // and when the sidebar is toggled.
  React.useEffect(() => {
    window.dispatchEvent(new Event("resize"));
  }, [zoom]);

  switch (dashboard.getIn(["json", "layoutType"])) {
    case "infinite":
      if (components.filter(c => c.get("fullScreen")).count() > 0) {
        DashboardUtils.lockPageScroll();
      } else {
        DashboardUtils.enablePageScroll();
      }

      // NOTE: this wrapping div is necessary.
      // Otherwise, the grid layout container does not apply its containerPadding to the bottom.
      // The classic background is painted by an ancestor backdrop in app.js (behind both the
      // empty-state fallback and this content), so it isn't applied here.
      return <div
          data-test-id="dashboard-zoom-container"
          style={{
            // Percentages resolve against the parent through this element's own zoom, so
            // 100% still fills the area available rather than overflowing it by `zoom`.
            width: "100%",
            height: "100%",
            zoom
          }}>
        {(cachedTabIds || Immutable.List()).map(tabId => {
          const isActiveTab = tabId === activeTabId;
          return (
              <div
                  key={"tab-grid-" + tabId}
                  style={{
                    display: isActiveTab ? "block" : "none",
                    width: "100%",
                    height: "100%"
                  }}>
                <TabGrid
                    components={componentsByTab.get(tabId, Immutable.List())}
                    idToData={idToDataByTab.get(tabId, Immutable.Map())}
                    uiStateByComponentId={uiStateByTab.get(tabId, Immutable.Map())}
                    componentTypeToTemplate={componentTypeToTemplate}
                    componentIdToLoadId={componentIdToLoadId}
                    isEditing={isEditing}
                    // The grid now measures itself in the zoomed coordinate space, where
                    // every length is `zoom` times larger, so the row has to shrink by the
                    // same factor for panels to keep their physical size.
                    rowHeight={rowHeight / zoom}
                    zoom={zoom}
                    theme={theme}
                    sidebarWidth={sidebarWidth}
                    toolbarBottomY={toolbarBottomY}
                    componentRefs={isActiveTab ? componentRefs : null}
                    handleComponentChange={handleComponentChange}
                    handleLayoutChange={isActiveTab ? handleLayoutChange : noop}
                    handleParentConfigChange={handleParentConfigChange}
                    handleDeleteComponentRequest={handleDeleteComponentRequest}
                    dashboardLayoutType={dashboard.getIn(["json", "layoutType"])}
                    parentConfig={dashboard.getIn(["json", "dataConfig"])}
                    queueDataLoad={queueDataLoad}
                    clearDataLoad={clearDataLoad}
                    onRequestDialog={onRequestDialog}
                    onRequestEdit={onRequestEdit}
                    onRequestComponentReload={onRequestComponentReload}
                    onDuplicateClick={onDuplicateClick}
                    onCaptureUiStateRef={onCaptureUiStateRef}
                    onComponentLoadStart={onComponentLoadStart}
                    onComponentLoadComplete={onComponentLoadComplete}
                    isPresentationMode={isPresentationMode}
                    panelColor={panelColor} />
              </div>
          );
        }).toArray()}
      </div>;
    case "window-only": {
      const idToData = idToDataByTab.get(activeTabId, Immutable.Map());
      const uiStateByComponentId = uiStateByTab.get(activeTabId, Immutable.Map());
      const component = components.get(0);
      if (component) {
        const template = componentTypeToTemplate.get(component.get("type"));
        const dataId = DashboardUtils.getDataId(componentIdToLoadId, component);
        const data = idToData.get(dataId);
        const componentId = component.get("id");

        return (
            <div
                data-test-id={"component-wrapper-" + component.get("id")}
                ref={el => {
                  if (el) {
                    componentRefs.current = componentRefs.current.set(componentId, el);
                  } else {
                    componentRefs.current = componentRefs.current.delete(componentId);
                  }
                }}
                style={{flex: "1", padding: "1rem", overflow: "hidden"}}>
              <ComponentWrapper
                  key={component.get("id")}
                  component={component}
                  onComponentChange={handleComponentChange}
                  template={template}
                  data={data}
                  isEditing={isEditing}
                  componentIdToLoadId={componentIdToLoadId}
                  queueDataLoad={queueDataLoad}
                  clearDataLoad={clearDataLoad}
                  onRequestDialog={onRequestDialog}
                  enableCharts={false}
                  panelColor={panelColor}
                  onRequestDelete={handleDeleteComponentRequest}
                  onRequestEdit={onRequestEdit}
                  onRequestReload={onRequestComponentReload}
                  onDuplicateClick={onDuplicateClick}
                  dashboardType={dashboard.getIn(["json", "layoutType"])}
                  parentConfig={dashboard.getIn(["json", "dataConfig"])}
                  onParentConfigChange={handleParentConfigChange}
                  componentTypeToTemplate={componentTypeToTemplate}
                  uiStateSnapshot={uiStateByComponentId ? uiStateByComponentId.get(componentId) : undefined}
                  onCaptureUiStateRef={onCaptureUiStateRef}
                  onLoadStart={onComponentLoadStart}
                  onLoadComplete={onComponentLoadComplete}
                  isPresentationMode={isPresentationMode} />
            </div>);
      } else {
        return null;
      }
    }
    case "gamified": {
      const idToData = idToDataByTab.get(activeTabId, Immutable.Map());
      const layout = components.map(c => getLayoutForComponent(c, componentTypeToTemplate)).toArray();
      const stageBorderColor = hasOutOfBoundsPanels ? theme.palette.error.main : theme.palette.primary.main;
      return (
          <div data-test-id="gamified-zoom-container" css={css`
            zoom: ${zoom};
            /* Stays a plain 100%: a percentage resolves against the parent's box and is
               not multiplied by this element's own zoom, so dividing it here would shrink
               the scroll viewport to 1/zoom of the area available and clip the stage. */
            width: 100%;
            height: 100%;
            overflow: auto;
            /* Divided so the rendered gap stays STAGE_H_PADDING physical pixels at every
               zoom level, matching getGamifiedStageGeometry's own subtraction. */
            padding: ${isPresentationMode ? "0" : `${STAGE_H_PADDING / zoom}px`};
            background-color: ${theme.palette.background.card};
            /* Deliberately does NOT reset the zoom. The stage is sized in JS for the
               compensated coordinate space, so cancelling the zoom here alone would print
               it at 1/zoom of its intended size. */
            @media print {
              overflow: visible;
              padding: 0;
              background: none;
            }
          `}>
            <div data-test-id="gamified-stage" style={{
              position: "relative",
              width: isPresentationMode ? "100%" : gamifiedStageWidth,
              height: isPresentationMode ? "100%" : gamifiedStageHeight,
              flexShrink: 0,
              borderRadius: isPresentationMode ? 0 : 4,
              boxSizing: "border-box",
              ...(Object.keys(backgroundStyle).length === 0
                ? {backgroundColor: theme.palette.background.paper}
                : backgroundStyle),
            }}>
              {!isPresentationMode && (
                  <svg
                      aria-hidden="true"
                      className="hide-for-print"
                      style={{position: "absolute", top: 0, left: 0, width: "100%", height: "100%", pointerEvents: "none", overflow: "visible"}}>
                    <rect
                        x="1"
                        y="1"
                        width={gamifiedStageWidth - 2}
                        height={gamifiedStageHeight - 2}
                        rx="4"
                        fill="none"
                        stroke={stageBorderColor}
                        strokeWidth="2"
                        strokeDasharray="8 7"
                    />
                  </svg>
              )}
              {!isPresentationMode && (
                  <div
                      className="hide-for-print"
                      style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    fontSize: 11,
                    color: theme.palette.primary.contrastText,
                    backgroundColor: stageBorderColor,
                    fontWeight: 600,
                    letterSpacing: "0.05em",
                    userSelect: "none",
                    borderRadius: 4,
                    padding: "2px 7px",
                    zIndex: 1,
                  }}>
                    {gamifiedRatio.w}:{gamifiedRatio.h}
                  </div>
              )}
              <GridLayout
                  className="layout"
                  cols={dashboardColumns}
                  rowHeight={gamifiedRowHeight}
                  width={gamifiedStageWidth}
                  transformScale={zoom}
                  layout={layout}
                  margin={[12 / zoom, 12 / zoom]}
                  containerPadding={[25 / zoom, 20 / zoom]}
                  onLayoutChange={handleGamifiedLayoutChange}
                  resizeHandles={isEditing ? dragHandles : noDragHandles}
                  draggableHandle=".cube19-drag-handle">
                {components.map(component => {
                  const template = componentTypeToTemplate.get(component.get("type"));
                  const data = idToData.get(DashboardUtils.getDataId(componentIdToLoadId, component));
                  const componentId = component.get("id");

                  return (
                      <div key={"component-" + component.get("id")} css={componentWrapperStyle}>
                        <div
                            ref={el => {
                              if (el) {
                                componentRefs.current = componentRefs.current.set(componentId, el);
                              } else {
                                componentRefs.current = componentRefs.current.delete(componentId);
                              }
                            }}
                            css={componentStyle}
                            data-test-id={"component-wrapper-" + component.get("id")}>
                          <ComponentWrapper
                              key={component.get("id")}
                              component={component}
                              onComponentChange={handleComponentChange}
                              template={template}
                              data={data}
                              isEditing={isEditing}
                              componentIdToLoadId={componentIdToLoadId}
                              queueDataLoad={queueDataLoad}
                              clearDataLoad={clearDataLoad}
                              onRequestDialog={onRequestDialog}
                              onRequestDelete={handleDeleteComponentRequest}
                              onRequestEdit={onRequestEdit}
                              enableCharts={template.get("type") === "UiGrid"}
                              panelColor={panelColor}
                              onRequestReload={onRequestComponentReload}
                              onDuplicateClick={onDuplicateClick}
                              dashboardType={dashboard.getIn(["json", "type"])}
                              parentConfig={dashboard.getIn(["json", "dataConfig"])}
                              onParentConfigChange={handleParentConfigChange}
                              componentTypeToTemplate={componentTypeToTemplate}
                              isGamified
                              onLoadStart={onComponentLoadStart}
                              onLoadComplete={onComponentLoadComplete}
                              isPresentationMode={isPresentationMode} />
                        </div>
                      </div>
                  );
                })}
              </GridLayout>
            </div>
          </div>
      );
    }
    default:
      return "unknown layout type: " + dashboard.getIn(["json", "layoutType"]);
  }
});

const getLayoutForComponent = (component, componentTypeToTemplate) => {
  const layout = component.get("layout", Immutable.Map());
  const template = componentTypeToTemplate.get(component.get("type"));
  const templateMin = template.getIn(["layout", "min"]);
  const templateMax = template.getIn(["layout", "max"]);

  return {
    i: "component-" + component.get("id"),
    x: layout.get("x"),
    y: layout.get("y"),
    w: layout.get("width"),
    h: layout.get("height"),
    minW: templateMin.get("width"),
    minH: templateMin.get("height"),
    maxW: templateMax.get("width"),
    maxH: templateMax.get("height")
  };
};

const TabGrid = React.memo(({
  components,
  zoom = 1,
  idToData,
  uiStateByComponentId,
  componentTypeToTemplate,
  componentIdToLoadId,
  isEditing,
  rowHeight,
  theme,
  sidebarWidth,
  toolbarBottomY,
  componentRefs,
  handleComponentChange,
  handleLayoutChange,
  handleParentConfigChange,
  handleDeleteComponentRequest,
  dashboardLayoutType,
  parentConfig,
  queueDataLoad,
  clearDataLoad,
  onRequestDialog,
  onRequestEdit,
  onRequestComponentReload,
  onDuplicateClick,
  onCaptureUiStateRef,
  onComponentLoadStart,
  onComponentLoadComplete,
  isPresentationMode = false,
  panelColor = null
}) => {
  const layout = React.useMemo(
      () => components.map(c => getLayoutForComponent(
          c, componentTypeToTemplate, {columns: dashboardColumns, rowHeight})).toArray(),
      [components, componentTypeToTemplate, rowHeight]);

  const setComponentRef = (componentId, el) => {
    if (!componentRefs) return;
    if (el) {
      componentRefs.current = componentRefs.current.set(componentId, el);
    } else {
      componentRefs.current = componentRefs.current.delete(componentId);
    }
  };

  return (
      <SizedGridLayout
          className="layout"
          cols={48}
          rowHeight={rowHeight}
          layout={layout}
          transformScale={zoom}
          // Divided so the gaps stay the same physical size at every zoom, leaving zoom to
          // scale the panel contents rather than the space between panels.
          margin={[12 / zoom, 12 / zoom]}
          containerPadding={[25 / zoom, 20 / zoom]}
          onLayoutChange={handleLayoutChange}
          resizeHandles={isEditing ? dragHandles : noDragHandles}
          draggableHandle=".cube19-drag-handle">
        {components.map(component => {
          const template = componentTypeToTemplate.get(component.get("type"));
          const data = idToData.get(DashboardUtils.getDataId(componentIdToLoadId, component));
          const componentId = component.get("id");
          const isFullScreen = component.get("fullScreen");

          const componentWrapper = (
              <ComponentWrapper
                  key={component.get("id")}
                  component={component}
                  onComponentChange={handleComponentChange}
                  template={template}
                  data={data}
                  isEditing={isEditing}
                  componentIdToLoadId={componentIdToLoadId}
                  queueDataLoad={queueDataLoad}
                  clearDataLoad={clearDataLoad}
                  onRequestDialog={onRequestDialog}
                  onRequestDelete={handleDeleteComponentRequest}
                  onRequestEdit={onRequestEdit}
                  enableCharts={template.get("type") === "UiGrid"}
                  panelColor={panelColor}
                  onRequestReload={onRequestComponentReload}
                  onDuplicateClick={onDuplicateClick}
                  dashboardType={dashboardLayoutType}
                  parentConfig={parentConfig}
                  onParentConfigChange={handleParentConfigChange}
                  componentTypeToTemplate={componentTypeToTemplate}
                  uiStateSnapshot={uiStateByComponentId ? uiStateByComponentId.get(componentId) : undefined}
                  onCaptureUiStateRef={onCaptureUiStateRef}
                  onLoadStart={onComponentLoadStart}
                  onLoadComplete={onComponentLoadComplete}
                  isPresentationMode={isPresentationMode} />
          );

          if (isFullScreen) {
            return (
                <div
                    key={"component-" + component.get("id")}
                    ref={el => setComponentRef(componentId, el)}
                    css={fullScreenStyle(theme, sidebarWidth, toolbarBottomY)}
                    data-test-id={"component-wrapper-" + component.get("id")}>
                  {componentWrapper}
                </div>
            );
          }

          return (
              <div key={"component-" + component.get("id")} css={componentWrapperStyle}
                   // Transparent panels (Text & Image) have no surface, so the RGL resize
                   // handle's default translucent-dark square shows over the panel's image.
                   // Give the handle the card colour via this custom property instead.
                   style={template.get("variant") === "transparent"
                       ? {"--resize-handle-bg": theme.palette.background.card}
                       : undefined}>
                <div
                    ref={el => setComponentRef(componentId, el)}
                    css={componentStyle}
                    data-test-id={"component-wrapper-" + component.get("id")}>
                  {componentWrapper}
                </div>
              </div>
          );
        })}
      </SizedGridLayout>
  );
});

const ComponentWrapper = React.memo(({
  component,
  onComponentChange,
  template,
  data,
  parentConfig,
  onParentConfigChange,
  isEditing,
  componentIdToLoadId,
  clearDataLoad,
  onRequestDialog,
  queueDataLoad,
  onRequestDelete,
  onRequestEdit,
  onRequestReload,
  componentTypeToTemplate,
  dashboardType,
  onDuplicateClick,
  enableCharts,
  uiStateSnapshot,
  onCaptureUiStateRef,
  onLoadStart,
  onLoadComplete,
  isGamified = false,
  isPresentationMode = false,
  panelColor = null
}) => {
  const {ErrorBoundary, didCatch, reset} = useErrorBoundary();
  const ReactComponent = template.get("getReactComponent")();
  // Gamified dashboards have no expand/fullscreen affordance for individual panels.
  const canFullScreen = template.get("canFullScreen", false) && !isGamified;
  const componentId = component.get("id");

  const handleReloadComponent = React.useCallback(() => {
    onRequestReload(componentId);
  }, [componentId, onRequestReload]);

  const handleDataConfigChange = React.useCallback((componentId, newDataConfig) => {
    onComponentChange(componentId, component => {
      if (typeof newDataConfig === "function") {
        return component.update("dataConfig", newDataConfig);
      } else {
        return component.set("dataConfig", newDataConfig);
      }
    });
  }, [onComponentChange]);

  const handleTitleChange = React.useCallback((componentId, newTitle) => {
    onComponentChange(componentId, component => component.set("title", newTitle));
  }, [onComponentChange]);

  const handleFullScreen = React.useCallback((componentId, fullScreen) => {
    if (fullScreen) {
      onComponentChange(componentId, component => component.set("fullScreen", true));
      DashboardUtils.lockPageScroll();
    } else {
      onComponentChange(componentId, component => component.set("fullScreen", false));
      DashboardUtils.enablePageScroll();
    }
  }, [onComponentChange]);

  const componentLoadId = componentIdToLoadId.get(componentId, 0);
  const [dimRef, dimensions] = useDimensions();
  const downloadFnRef = React.useRef();

  // A panel that reports its own load cannot once it has crashed while rendering, and a
  // scheduled report left waiting on it fails outright instead of printing the error.
  React.useEffect(() => {
    if (didCatch && template.get("requiresFurtherLoad") && onLoadComplete) {
      onLoadComplete(DashboardUtils.dataId(componentLoadId, componentId));
    }
  }, [didCatch, template, onLoadComplete, componentLoadId, componentId]);

  if (didCatch) {
    return <ComponentTile variant={template.get("variant")} panelColor={panelColor}>
      <BasicComponentTitleBar
          componentId={component.get("id")}
          onRemoveClick={onRequestDelete}
          onEditClick={onRequestEdit}
          canFullScreen={canFullScreen}
          isFullScreen={component.get("fullScreen")}
          onFullScreenClick={handleFullScreen}
          isEditing={isEditing}
          titleIsEditable={template.get("titleIsEditable", true)}
          title={template.get("getTitle")(component)}
          variant={template.get("variant")}
          onTitleChange={handleTitleChange} />
      <Overlays.Error onClick={reset} coverOtherContent={false}>
        Error displaying data, click to try again
      </Overlays.Error>
    </ComponentTile>;
  } else {
    let content;
    if (Rata.isLoading(data)) {
      content = <Overlays.Loading coverOtherContent={false} />;
    } else if (Rata.hasError(data)) {
      const error = Rata.getError(data);
      let message;
      if (typeof error === "string") {
        message = error;
      } else if (error.responseJSON && error.responseJSON.message) {
        message = <p style={{fontSize: "0.6rem", lineHeight: "0.8rem", margin: 0}}>
          {error.responseJSON.message}
          {error.responseJSON.errorId && <>Error ID: {error.responseJSON.errorId}</>}
        </p>;
      } else {
        message = "Error loading data, click to try again";
      }
      content = <Overlays.Error onClick={handleReloadComponent} coverOtherContent={false}>
        {message}
      </Overlays.Error>;
    } else {
      /* Key change was introduced as part of BA-9647 (ag-grid update) to handle the issues with column editing
      when switching to non pivot mode for AsyncServerGrids. Revert to original component rendering
      if we get a fix for it. */
      const isAsyncServerGrid = template.get("type") === "AsyncServerGrid";
      const isPivotMode = component.getIn(["dataConfig", "gridProps", "pivotMode"], false);
      content = <ReactComponent
          key={"component-" + componentId + "-" + componentLoadId
              + (isAsyncServerGrid && isPivotMode ? `-pivot` : '')}
          componentId={componentId}
          dimensions={dimensions}
          dashboardType={dashboardType}
          downloadFnRef={downloadFnRef}
          title={template.get("getTitle")(component)}
          config={component.get("dataConfig")}
          onConfigChange={handleDataConfigChange}
          data={data}
          isEditing={isEditing}
          enableCharts={enableCharts}
          panelColor={panelColor}
          component={component}
          componentLoadId={componentLoadId}
          onRequestDialog={onRequestDialog}
          onRequestReload={handleReloadComponent}
          queueDataLoad={queueDataLoad}
          clearDataLoad={clearDataLoad}
          componentTypeToTemplate={componentTypeToTemplate}
          parentConfig={parentConfig}
          onParentConfigChange={onParentConfigChange}
          uiStateSnapshot={uiStateSnapshot}
          onCaptureUiStateRef={onCaptureUiStateRef}
          onLoadStart={onLoadStart}
          onLoadComplete={onLoadComplete}
          isPresentationMode={isPresentationMode}/>;
    }

    return <ErrorBoundary>
      <ComponentTile variant={template.get("variant")} panelColor={panelColor}>
        <BasicComponentTitleBar
            config={component.get("dataConfig")}
            inheritKeys={component.getIn(["dataConfig", "inheritKeys"])}
            componentId={componentId}
            onRemoveClick={onRequestDelete}
            onEditClick={onRequestEdit}
            onDownloadClickRef={downloadFnRef}
            canDownload={template.get("canDownload", false)}
            isClickThrough={component.getIn(["dataConfig", "isClickThrough"], false)}
            canFullScreen={canFullScreen}
            isFullScreen={component.get("fullScreen")}
            enableCharts={enableCharts}
            onFullScreenClick={handleFullScreen}
            onDuplicateClick={onDuplicateClick}
            isEditing={isEditing}
            titleIsEditable={template.get("titleIsEditable", true)}
            title={template.get("getTitle")(component)}
            variant={template.get("variant")}
            onTitleChange={handleTitleChange} />
        <div
            ref={dimRef} style={{
          position: "relative",
          flex: 1,
          width: "100%",
          overflow: template.get("variant") !== "standard" ? "hidden" : "visible",
          paddingTop: template.get("getTitle")(component) || template.get("titleIsEditable", true) ? 34 : 0
        }}>
          {content}
        </div>
      </ComponentTile>
    </ErrorBoundary>;
  }
});

export default DashboardDisplay;
